// frontend/src/store/messengerStore.ts
// FB-style messenger dock state (Messenger + Feed rebuild).
//
// Single source of truth for: which conversation windows are open in the dock
// (max 3 non-minimized), the unread badge totals, and live typing indicators.
// Reset on principal switch (impersonate / demo) — see authStore call sites —
// so dock windows + unread from principal A never leak to principal B.
import { create } from 'zustand'

export interface MessengerWindow {
  conversationId: number
  minimized: boolean
}

export interface MessengerState {
  dockOpen: boolean
  windows: MessengerWindow[]
  totalUnread: number
  unreadByConv: Record<number, number>
  typingByConv: Record<number, number[]>

  openConversation: (id: number) => void
  closeWindow: (id: number) => void
  minimizeWindow: (id: number, v: boolean) => void
  setDockOpen: (v: boolean) => void
  applyUnread: (convId: number, total: number) => void
  setTotalUnread: (n: number) => void
  setTyping: (convId: number, userId: number, on: boolean) => void
  reset: () => void
}

// Max simultaneously-OPEN (non-minimized) windows. Opening a 4th minimizes the
// oldest open one rather than rejecting — matches Facebook Messenger.
const MAX_OPEN_WINDOWS = 3

export const useMessengerStore = create<MessengerState>((set) => ({
  dockOpen: false,
  windows: [],
  totalUnread: 0,
  unreadByConv: {},
  typingByConv: {},

  openConversation: (id) => set((s) => {
    const existing = s.windows.find((w) => w.conversationId === id)
    if (existing) {
      // Promote an existing (possibly minimized) window to the front, un-minimized.
      return {
        windows: s.windows.map((w) =>
          w.conversationId === id ? { ...w, minimized: false } : w
        ),
      }
    }
    let windows = [...s.windows, { conversationId: id, minimized: false }]
    const open = windows.filter((w) => !w.minimized)
    if (open.length > MAX_OPEN_WINDOWS) {
      // Minimize the oldest open window that isn't the one we just opened.
      const oldest = windows.find((w) => !w.minimized && w.conversationId !== id)
      if (oldest) {
        windows = windows.map((w) =>
          w.conversationId === oldest.conversationId ? { ...w, minimized: true } : w
        )
      }
    }
    return { windows }
  }),

  closeWindow: (id) => set((s) => ({
    windows: s.windows.filter((w) => w.conversationId !== id),
  })),

  minimizeWindow: (id, v) => set((s) => ({
    windows: s.windows.map((w) => (w.conversationId === id ? { ...w, minimized: v } : w)),
  })),

  setDockOpen: (v) => set({ dockOpen: v }),

  applyUnread: (convId, total) => set((s) => {
    const unreadByConv = { ...s.unreadByConv, [convId]: Math.max(0, total) }
    const totalUnread = Object.values(unreadByConv).reduce((a, b) => a + b, 0)
    return { unreadByConv, totalUnread }
  }),

  setTotalUnread: (n) => set({ totalUnread: Math.max(0, n) }),

  setTyping: (convId, userId, on) => set((s) => {
    const curr = s.typingByConv[convId] ?? []
    const next = on
      ? Array.from(new Set([...curr, userId]))
      : curr.filter((uid) => uid !== userId)
    return { typingByConv: { ...s.typingByConv, [convId]: next } }
  }),

  reset: () => set({
    dockOpen: false,
    windows: [],
    totalUnread: 0,
    unreadByConv: {},
    typingByConv: {},
  }),
}))
